/**
 * Image uploader component with drag & drop support.
 */
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Image as ImageIcon, X } from 'lucide-react';

interface ImageUploaderProps {
  onImageSelect: (file: File) => void;
  selectedImage?: File | null;
  onClear?: () => void;
  disabled?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImageSelect,
  selectedImage,
  onClear,
  disabled = false,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onImageSelect(acceptedFiles[0]);
      }
    },
    [onImageSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    },
    multiple: false,
    disabled,
  });

  return (
    <div className="w-full">
      {!selectedImage ? (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            transition-colors duration-200
            ${
              isDragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input {...getInputProps()} />
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-2">
            {isDragActive ? 'Déposez l\'image ici' : 'Glissez-déposez une image d\'exercice'}
          </p>
          <p className="text-sm text-gray-500">
            ou cliquez pour sélectionner un fichier
          </p>
          <p className="text-xs text-gray-400 mt-2">
            PNG, JPG, GIF jusqu'à 10MB
          </p>
        </div>
      ) : (
        <div className="relative border-2 border-gray-300 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-blue-500" />
              <span className="font-medium text-gray-700">{selectedImage.name}</span>
              <span className="text-sm text-gray-500">
                ({(selectedImage.size / 1024 / 1024).toFixed(2)} MB)
              </span>
            </div>
            {onClear && (
              <button
                onClick={onClear}
                className="text-red-500 hover:text-red-700 transition-colors"
                disabled={disabled}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <img
            src={URL.createObjectURL(selectedImage)}
            alt="Preview"
            className="max-h-64 mx-auto rounded"
          />
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
